// ============================================================================
// @torre/expenses — Due diligence de plataformas (CC-CONSOLE #956)
// ============================================================================
// Además de cuánto cuesta, de cada plataforma se registra:
//
//   · usedBy       → qué repos y qué apps dentro de cada repo la usan
//   · tenants      → a qué tenants / clientes da servicio
//   · shared       → si una misma cuenta se comparte entre repos o tenants
//   · licensing    → cómo se licencia: por uso, por asiento, ambos, tarifa fija o gratis
//   · invoiceType  → qué comprobante esperar: CFDI, factura extranjera, recibo o nada
//   · paidBy       → quién paga la cuenta (Coding Cloud, el cliente, una persona…)
//
// Todos opcionales: una plataforma sin ellos sigue siendo válida. Lógica pura.
// ============================================================================

import type { Platform } from "./index";

export const PLATFORM_LICENSING = ["uso", "asiento", "ambos", "fijo", "gratis"] as const;
export type PlatformLicensing = (typeof PLATFORM_LICENSING)[number];

export const PLATFORM_LICENSING_LABELS: Record<PlatformLicensing, { es: string; en: string }> = {
  uso: { es: "Por uso", en: "Usage-based" },
  asiento: { es: "Por asiento", en: "Per seat" },
  ambos: { es: "Asiento + uso", en: "Seat + usage" },
  fijo: { es: "Tarifa fija", en: "Flat fee" },
  gratis: { es: "Gratis", en: "Free" },
};

export const PLATFORM_INVOICE_TYPES = ["cfdi", "factura_extranjera", "recibo", "sin_factura"] as const;
export type PlatformInvoiceType = (typeof PLATFORM_INVOICE_TYPES)[number];

export const PLATFORM_INVOICE_TYPE_LABELS: Record<PlatformInvoiceType, { es: string; en: string }> = {
  cfdi: { es: "CFDI (México)", en: "CFDI (Mexico)" },
  factura_extranjera: { es: "Factura extranjera", en: "Foreign invoice" },
  recibo: { es: "Recibo / comprobante de pago", en: "Receipt" },
  sin_factura: { es: "Sin factura", en: "No invoice" },
};

/** Un repo que usa la plataforma y las apps de ese repo que la tocan. */
export interface PlatformUsage {
  repo: string;
  apps: string[];
  purpose?: string | null;
}

export interface PlatformDiligence {
  usedBy?: PlatformUsage[] | null;
  tenants?: string[] | null;
  shared?: boolean | null;
  licensing?: PlatformLicensing | null;
  invoiceType?: PlatformInvoiceType | null;
  paidBy?: string | null;
  /** YYYY-MM-DD de la última revisión. */
  reviewedAt?: string | null;
}

const cleanList = (v: unknown): string[] =>
  Array.from(new Set((Array.isArray(v) ? v : String(v ?? "").split(",")).map((x) => String(x).trim()).filter(Boolean)));

/**
 * Deja los campos de due diligence en su forma guardada: listas sin vacíos ni
 * repetidos y `shared` derivado cuando no viene (más de un repo o tenant).
 */
export function normalizePlatformDiligence<T extends Partial<Platform>>(input: T): T {
  const out: T = { ...input };
  if (out.usedBy !== undefined) {
    out.usedBy = (Array.isArray(out.usedBy) ? out.usedBy : [])
      .map((u) => ({ repo: String(u?.repo ?? "").trim(), apps: cleanList(u?.apps), purpose: u?.purpose ? String(u.purpose).trim() : null }))
      .filter((u) => u.repo);
  }
  if (out.tenants !== undefined) out.tenants = cleanList(out.tenants);
  if (out.shared == null && (out.usedBy !== undefined || out.tenants !== undefined)) {
    out.shared = (out.usedBy?.length ?? 0) > 1 || (out.tenants?.length ?? 0) > 1;
  }
  return out;
}

export function validatePlatformDiligence(input: Partial<Platform>): { valid: boolean; errors: string[] } {
  const errors: string[] = [];
  if (input.licensing != null && !PLATFORM_LICENSING.includes(input.licensing)) errors.push("licensing: uso, asiento, ambos, fijo o gratis");
  if (input.invoiceType != null && !PLATFORM_INVOICE_TYPES.includes(input.invoiceType)) {
    errors.push("invoiceType: cfdi, factura_extranjera, recibo o sin_factura");
  }
  if (input.usedBy != null && !Array.isArray(input.usedBy)) errors.push("usedBy: debe ser una lista");
  if (input.tenants != null && !Array.isArray(input.tenants)) errors.push("tenants: debe ser una lista");
  if (input.reviewedAt != null && input.reviewedAt !== "" && !/^\d{4}-\d{2}-\d{2}$/.test(input.reviewedAt)) errors.push("reviewedAt: YYYY-MM-DD");
  return { valid: errors.length === 0, errors };
}

/** Texto de una línea por repo: "WAYAK: apps/v5, functions". */
export function usageToLines(usedBy: readonly PlatformUsage[] | null | undefined): string {
  return (usedBy || []).map((u) => (u.apps.length ? `${u.repo}: ${u.apps.join(", ")}` : u.repo)).join("\n");
}

/** Inverso de `usageToLines` (conserva el propósito de los repos que ya existían). */
export function linesToUsage(text: string, previous: readonly PlatformUsage[] = []): PlatformUsage[] {
  return text
    .split("\n")
    .map((line) => {
      const [repo, apps = ""] = line.split(/:(.*)/s).map((x) => (x ?? "").trim());
      const before = previous.find((p) => p.repo === repo);
      return { repo, apps: cleanList(apps), purpose: before?.purpose ?? null };
    })
    .filter((u) => u.repo);
}
